import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import './RodmanAnimation.css';

// Enhanced thinking animation with Claude-style pulsing dots
export const ThinkingIndicator = ({ text = "Analyzing" }) => {
    return (
        <div className="thinking-container">
            <div className="thinking-dots">
                <span className="thinking-dot"></span>
                <span className="thinking-dot"></span>
                <span className="thinking-dot"></span>
            </div>
            <div className="thinking-text">{text}</div>
        </div>
    );
};

// Typewriter effect component for streaming text with Markdown support
export const TypewriterText = ({ 
    text, 
    speed = 30, 
    onComplete,
    isMarkdown = false,
    startDelay = 0,
    markdownComponents = {} 
}) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (startDelay > 0) {
            const delayTimer = setTimeout(() => {
                setIsTyping(true);
            }, startDelay);
            return () => clearTimeout(delayTimer);
        } else {
            setIsTyping(true);
        }
    }, [startDelay]);

    useEffect(() => {
        if (!isTyping || !text) return;

        if (currentIndex < text.length) {
            intervalRef.current = setTimeout(() => {
                // Stream word by word for more natural feel
                const nextSpace = text.indexOf(' ', currentIndex);
                const nextChunk = nextSpace === -1 
                    ? text.substring(currentIndex)
                    : text.substring(currentIndex, nextSpace + 1);
                
                setDisplayedText(prev => prev + nextChunk);
                setCurrentIndex(nextSpace === -1 ? text.length : nextSpace + 1);
            }, speed);
        } else if (onComplete) {
            onComplete();
        }

        return () => {
            if (intervalRef.current) {
                clearTimeout(intervalRef.current);
            }
        };
    }, [currentIndex, text, speed, onComplete, isTyping]);

    // Render with Markdown if enabled
    if (isMarkdown) {
        return (
            <span className="typewriter-text">
                <ReactMarkdown 
                    components={{
                        p: ({ children }) => <span>{children}</span>,
                        ...markdownComponents
                    }}
                >
                    {displayedText}
                </ReactMarkdown>
                {currentIndex < text.length && (
                    <span className="typewriter-cursor">|</span>
                )}
            </span>
        );
    }

    // Plain text with cursor
    return (
        <span className="typewriter-text">
            {displayedText}
            {currentIndex < text.length && (
                <span className="typewriter-cursor">|</span>
            )}
        </span>
    );
};

// Enhanced message component with animation states
export const AnimatedMessage = ({ 
    message, 
    onRenderComplete,
    enableTypewriter = true,
    scrollBehavior = 'top' // 'top', 'bottom', or 'none'
}) => {
    const [state, setState] = useState('thinking'); // 'thinking', 'typing', 'complete'
    const [isVisible, setIsVisible] = useState(false);
    const messageRef = useRef(null);
    const hasScrolledRef = useRef(false);

    // Manage scroll position
    useEffect(() => {
        if (messageRef.current && !hasScrolledRef.current && scrollBehavior !== 'none') {
            hasScrolledRef.current = true;
            
            // Use requestAnimationFrame for smoother scroll
            requestAnimationFrame(() => {
                if (scrollBehavior === 'top') {
                    // Scroll to top of message, keeping it at the top of viewport
                    messageRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                } else if (scrollBehavior === 'bottom') {
                    // Traditional scroll to bottom
                    messageRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'end' 
                    });
                }
            });
        }
    }, [scrollBehavior, state]);

    // Animation sequence
    useEffect(() => {
        // Start with fade in
        const fadeTimer = setTimeout(() => setIsVisible(true), 100);

        // Transition from thinking to typing
        const thinkingTimer = setTimeout(() => {
            setState('typing');
        }, 1500);

        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(thinkingTimer);
        };
    }, []);

    const handleTypewriterComplete = useCallback(() => {
        setState('complete');
        if (onRenderComplete) {
            onRenderComplete();
        }
    }, [onRenderComplete]);

    return (
        <div 
            ref={messageRef}
            className={`animated-message ${message.type} ${isVisible ? 'visible' : ''} state-${state}`}
        >
            <div className="animated-message-content">
                {state === 'thinking' && message.type === 'rodman' && (
                    <ThinkingIndicator />
                )}
                
                {state !== 'thinking' && (
                    <div className="message-text-wrapper">
                        {enableTypewriter && state === 'typing' ? (
                            <TypewriterText 
                                text={message.content}
                                speed={20}
                                onComplete={handleTypewriterComplete}
                                isMarkdown={message.type === 'rodman'}
                            />
                        ) : (
                            <span className="static-text">{message.content}</span>
                        )}
                    </div>
                )}

                {state === 'complete' && (
                    <div className="message-meta">
                        <span className="message-time">
                            {new Date().toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                            })}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Main chat container with enhanced animations
export const RodmanChatAnimation = ({ messages = [], onSendMessage }) => {
    const [animatedMessages, setAnimatedMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const chatContainerRef = useRef(null);
    const lastMessageRef = useRef(null);

    // Add messages with animation control
    useEffect(() => {
        if (messages.length > animatedMessages.length) {
            const newMessage = messages[messages.length - 1];
            setAnimatedMessages(prev => [...prev, { ...newMessage, animating: true }]);
            
            if (newMessage.type === 'rodman') {
                setIsProcessing(false);
            }
        }
    }, [messages, animatedMessages.length]);

    const handleSend = () => {
        if (inputValue.trim() && onSendMessage) {
            setIsProcessing(true);
            onSendMessage(inputValue);
            setInputValue('');
        }
    };

    const handleMessageComplete = (index) => {
        setAnimatedMessages(prev => 
            prev.map((msg, i) => 
                i === index ? { ...msg, animating: false } : msg
            )
        );
    };

    // Prevent auto-scroll during text generation
    useEffect(() => {
        if (chatContainerRef.current) {
            // Disable browser's built-in scroll anchoring
            chatContainerRef.current.style.overflowAnchor = 'none';
        }
    }, []);

    return (
        <div className="rodman-chat-animation">
            <div className="chat-header-animation">
                <div className="header-glow"></div>
                <h2>RODMAN AI</h2>
                <p>Claude-Style Animation Demo</p>
            </div>

            <div 
                ref={chatContainerRef}
                className="chat-container-animation"
            >
                {animatedMessages.map((msg, index) => (
                    <AnimatedMessage
                        key={msg.id || index}
                        message={msg}
                        enableTypewriter={msg.type === 'rodman'}
                        scrollBehavior={index === animatedMessages.length - 1 ? 'top' : 'none'}
                        onRenderComplete={() => handleMessageComplete(index)}
                    />
                ))}
                
                {isProcessing && (
                    <div className="animated-message rodman visible">
                        <div className="animated-message-content">
                            <ThinkingIndicator />
                        </div>
                    </div>
                )}
                
                <div ref={lastMessageRef} />
            </div>

            <div className="chat-input-animation">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ask Rodman..."
                    disabled={isProcessing}
                    className="input-field-animation"
                />
                <button 
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isProcessing}
                    className={`send-button-animation ${inputValue.trim() ? 'active' : ''}`}
                >
                    {isProcessing ? '...' : '→'}
                </button>
            </div>
        </div>
    );
};

export default RodmanChatAnimation;